import React from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useTranslation } from 'react-i18next';
import { format, parse } from 'date-fns';
import { LocalTimeFieldType } from '@/type';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { buildFormFieldLabel } from '@/util';
import { StringSchema } from 'yup';
import InputAdornment from '@mui/material/InputAdornment';
import { hu } from 'date-fns/locale';

interface LocalTimeControllerProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  localTimeFieldType: LocalTimeFieldType;
  disabled: boolean;
}

const TIME_FORMAT = 'HH:mm:ss';
const TIME_FORMAT_NO_SEC = 'HH:mm';

const LocalTimeController = <TFieldValues extends FieldValues = FieldValues>({
  control,
  localTimeFieldType,
  disabled = false,
}: LocalTimeControllerProps<TFieldValues>) => {
  const { t } = useTranslation();

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={hu}>
      <Controller
        name={localTimeFieldType.name as any}
        control={control}
        defaultValue={localTimeFieldType.defaultValue as any}
        rules={{
          required: localTimeFieldType.required ? t('common.form.required_field') : false,
        }}
        render={({ field, fieldState: { error } }) => (
          <TimePicker
            {...field}
            className='timepicker-input'
            label={buildFormFieldLabel(
              t(localTimeFieldType.label),
              localTimeFieldType.validation as StringSchema,
            )}
            views={['hours', 'minutes']}
            timeSteps={{ minutes: 1 }}
            ampm={false}
            disabled={disabled}
            value={
              field.value
                ? parse(
                    field.value,
                    localTimeFieldType.disableSeconds ? TIME_FORMAT_NO_SEC : TIME_FORMAT,
                    new Date(),
                  )
                : null
            }
            onChange={(date) => {
              const localTime = date
                ? format(
                    date,
                    localTimeFieldType.disableSeconds ? TIME_FORMAT_NO_SEC : TIME_FORMAT,
                  )
                : '';
              field.onChange(localTime);
            }}
            sx={{ width: '100%' }}
            slotProps={{
              textField: {
                id: field.name,
                error: !!error,
                helperText: error ? error.message : undefined,
                InputLabelProps: {
                  shrink: true,
                },
                InputProps: {
                  startAdornment: localTimeFieldType.preFix ? (
                    <InputAdornment position='start'>
                      {localTimeFieldType.preFix}
                    </InputAdornment>
                  ) : null,
                },
              },
            }}
          />
        )}
      />
    </LocalizationProvider>
  );
};

export default LocalTimeController;
